<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatDelay, highlightVariables } from './followUpTime';

const props = defineProps({
  step: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    default: 0,
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  expanded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'toggleExpand',
  'togglePause',
  'duplicate',
  'remove',
]);

const { t } = useI18n();

const isTemplate = computed(() => props.step.message_type === 'template');

// Passo entregue no evento de entrada na etapa: não tem espera para mostrar.
const isInstant = computed(() => !!props.step.on_stage_enter);

// O "Dia N" saiu daqui na Task 4: virou redundante com o nó da
// FollowUpTimeline, que já mostra o dia acumulado na linha do tempo.
const delayLabel = computed(() =>
  t('FUNNEL_TIMELINE.WAITS', { t: formatDelay(props.step.delay_hours) })
);

const typeLabel = computed(() =>
  isTemplate.value
    ? t('FUNNEL_TIMELINE.TYPE_TEMPLATE')
    : t('FUNNEL_TIMELINE.TYPE_TEXT')
);

const channelInbox = computed(
  () => props.inboxes.find(inbox => inbox.id === props.step.inbox_id) || null
);

const bodyTokens = computed(() => highlightVariables(props.step.content));

const pauseIcon = computed(() =>
  props.step.paused ? 'i-lucide-play' : 'i-lucide-pause'
);

const pauseTooltip = computed(() =>
  props.step.paused ? t('FUNNEL_TIMELINE.RESUME') : t('FUNNEL_TIMELINE.PAUSE')
);

// Horário/tique fake da prévia — não é dado real, então fica fora do i18n
// (mesmo texto em qualquer idioma, como no WhatsApp de verdade).
const previewTimestamp = '10:32 ✓✓';
</script>

<template>
  <div
    :data-testid="`follow-up-step-card-${index}`"
    class="cursor-pointer rounded-xl border border-n-weak bg-n-solid-2 transition-all hover:border-n-brand/40 hover:shadow-md"
    :class="{
      'opacity-60': step.paused,
      'border-n-brand/50 ring-2 ring-n-brand/30': expanded,
    }"
    @click="emit('toggleExpand')"
  >
    <div class="flex flex-wrap items-center gap-2 px-3 pt-3">
      <span
        v-if="isInstant"
        class="flex items-center gap-1 rounded-md bg-n-brand/15 px-2 py-0.5 text-xs font-medium text-n-brand"
      >
        <span class="i-lucide-zap size-3" />
        {{ t('FUNNEL_TIMELINE.INSTANT') }}
      </span>
      <span
        v-else
        class="flex items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-12"
      >
        <span class="i-lucide-clock size-3" />
        {{ delayLabel }}
      </span>
      <span
        class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-12"
      >
        {{ typeLabel }}
      </span>
      <span
        v-if="channelInbox"
        class="flex items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-12"
      >
        <span class="i-lucide-send size-3" />
        {{ channelInbox.name }}
      </span>
      <span
        v-if="step.paused"
        class="rounded-md bg-n-amber-3 px-2 py-0.5 text-xs font-medium text-n-amber-11"
      >
        {{ t('FUNNEL_TIMELINE.PAUSED') }}
      </span>

      <div class="ml-auto flex items-center gap-1">
        <Button
          v-tooltip="pauseTooltip"
          variant="ghost"
          color="slate"
          size="sm"
          :icon="pauseIcon"
          data-testid="follow-up-step-card-pause"
          @click.stop="emit('togglePause')"
        />
        <Button
          v-tooltip="t('FUNNEL_TIMELINE.DUPLICATE')"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-copy"
          data-testid="follow-up-step-card-duplicate"
          @click.stop="emit('duplicate')"
        />
        <Button
          v-tooltip="t('FUNNEL_TIMELINE.DELETE')"
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          data-testid="follow-up-step-card-remove"
          @click.stop="emit('remove')"
        />
      </div>
    </div>

    <div class="px-3 pb-3 pt-2">
      <div class="flex justify-end">
        <div
          class="max-w-[85%] rounded-lg rounded-tr-none bg-[#d9fdd3] px-3 py-2 text-sm text-[#111b21] dark:bg-[#005c4b] dark:text-[#e9edef]"
        >
          <p v-if="isTemplate" class="flex items-center gap-1.5 font-medium">
            <span class="i-lucide-layout-template size-4 shrink-0" />
            {{ step.template_name }}
          </p>
          <p v-else class="whitespace-pre-wrap break-words">
            <template
              v-for="(token, tokenIndex) in bodyTokens"
              :key="tokenIndex"
            >
              <span
                v-if="token.type === 'var'"
                class="rounded bg-black/10 px-1 font-medium dark:bg-white/20"
                >{{ token.value }}</span
              >
              <template v-else>{{ token.value }}</template>
            </template>
          </p>
          <p class="mt-1 text-right text-[10px] opacity-60">
            {{ previewTimestamp }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
